import ModalBasicLayout from "./ModalBasicLayout";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import DialogActions from "@mui/material/DialogActions";
import ContainerModalActions from "../Containers/ContainerModalActions";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp } from "../../lib/redux";
import { startCreateAddonSpecialistPaymentLink } from "../../actions/payments";
import {
  MODAL_PERFORMANCE_SPECIALIST_ARIA_LABEL,
  MODAL_PERFORMANCE_SPECIALIST_TITLE,
  MODAL_PERFORMANCE_SPECIALIST_SUBTITLE,
  MODAL_PERFORMANCE_SPECIALIST_WHAT_YOU_GET_TITLE,
  MODAL_PERFORMANCE_SPECIALIST_FEATURE_1,
  MODAL_PERFORMANCE_SPECIALIST_FEATURE_2,
  MODAL_PERFORMANCE_SPECIALIST_FEATURE_3,
  MODAL_PERFORMANCE_SPECIALIST_FEATURE_4,
  MODAL_PERFORMANCE_SPECIALIST_PRICING_TITLE,
  MODAL_PERFORMANCE_SPECIALIST_PRICING_MAIN,
  MODAL_PERFORMANCE_SPECIALIST_PRICING_NOTE_1,
  MODAL_PERFORMANCE_SPECIALIST_PRICING_NOTE_2,
  MODAL_PERFORMANCE_SPECIALIST_CTA,
} from "../../i18n/keysTranslations";
import { BORDER_RADIUS } from "../../utils/constants";
import { BOX_SHADOW_THEME } from "../../utils/colors";
import { useCreditActionCosts } from "../../hooks/useCreditActionCosts";

const FEATURES = [
  MODAL_PERFORMANCE_SPECIALIST_FEATURE_1,
  MODAL_PERFORMANCE_SPECIALIST_FEATURE_2,
  MODAL_PERFORMANCE_SPECIALIST_FEATURE_3,
  MODAL_PERFORMANCE_SPECIALIST_FEATURE_4,
];

const ModalPerformanceMarketingSpecialist = ({
  modalOpen = false,
  onCloseModal = () => {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { adSpendFeePercentage } = useCreditActionCosts();

  const onHireSpecialist = async () => {
    const response = await dispatch(startCreateAddonSpecialistPaymentLink());
    if (response.ok && response.data?.url) {
      window.location.href = response.data.url;
    }
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      maxWidth="sm"
      fullWidth
      hasTitle={true}
      ariaLabel={t(MODAL_PERFORMANCE_SPECIALIST_ARIA_LABEL)}
      sxContent={{ pb: 0 }}
      showCloseButton={true}
    >
      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1, pb: 3 }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center" }}>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, mb: 1, lineHeight: 1.2 }}
          >
            {t(MODAL_PERFORMANCE_SPECIALIST_TITLE)}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              lineHeight: 1.6,
              maxWidth: 420,
              mx: "auto",
            }}
          >
            {t(MODAL_PERFORMANCE_SPECIALIST_SUBTITLE)}
          </Typography>
        </Box>

        {/* What you get */}
        <Box
          sx={(theme) => ({
            borderRadius: BORDER_RADIUS,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: BOX_SHADOW_THEME,
            p: 2.5,
          })}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              mb: 1.5,
              fontSize: "0.8rem",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "text.secondary",
            }}
          >
            {t(MODAL_PERFORMANCE_SPECIALIST_WHAT_YOU_GET_TITLE)}
          </Typography>
          <Box
            component="ul"
            sx={{
              m: 0,
              pl: 0,
              listStyle: "none",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {FEATURES.map((key, index) => (
              <Box
                key={index}
                component="li"
                sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
              >
                <Box
                  sx={(theme) => ({
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    backgroundColor: theme.palette.success.main,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    mt: "1px",
                  })}
                >
                  <Typography
                    sx={{
                      color: "#fff",
                      fontSize: "0.65rem",
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    ✓
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: "text.primary", lineHeight: 1.5 }}
                >
                  {t(key)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Pricing & conditions */}
        <Box
          sx={(theme) => ({
            borderRadius: BORDER_RADIUS,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: BOX_SHADOW_THEME,
            p: 2.5,
          })}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              mb: 1.5,
              fontSize: "0.8rem",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "text.secondary",
            }}
          >
            {t(MODAL_PERFORMANCE_SPECIALIST_PRICING_TITLE)}
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: "text.primary" }}
          >
            {t(MODAL_PERFORMANCE_SPECIALIST_PRICING_MAIN, {
              percentage: adSpendFeePercentage,
            })}
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {[
              MODAL_PERFORMANCE_SPECIALIST_PRICING_NOTE_2,
              MODAL_PERFORMANCE_SPECIALIST_PRICING_NOTE_1,
            ].map((key, index) => (
              <Typography
                key={index}
                variant="caption"
                sx={{
                  color: "text.secondary",
                  lineHeight: 1.5,
                  display: "block",
                }}
              >
                {t(key)}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>

      <DialogActions
        sx={{
          p: 0,
        }}
      >
        <ContainerModalActions
          sx={{
            pb: 3,
          }}
          hasPadding={true}
          propsPrimaryButton={{
            showButton: true,
            text: MODAL_PERFORMANCE_SPECIALIST_CTA,
            variant: "contained",
            onClick: onHireSpecialist,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalPerformanceMarketingSpecialist;
